/**
 * LineageTrack — shared horizontal scrub track for run lineage.
 *
 * Sprint-237 (FE-1 S2): extracted from LineageScrubber's track block
 * (extract-don't-mutate, sdd.md §4.2.1) so the new bottom
 * LineageTimelineStrip and the original scrubber share ONE track
 * implementation. Status colors are imported from RunTimeline's
 * canonical STATUS_COLORS map — never forked.
 *
 * Driven by the `LineageTimelineState` selection model
 * (hooks/useLineageTimeline.ts). Renders even for a single point
 * (one marker at 50%); click-to-scrub interaction requires ≥2 points.
 */

import { useCallback, useRef, useState } from 'react';
import type { LineageTimelineState } from '../../hooks/useLineageTimeline';
import { STATUS_COLORS } from './RunTimeline';

interface LineageTrackProps {
  timeline: LineageTimelineState;
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatAge(ts: number): string {
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

export function LineageTrack({ timeline }: LineageTrackProps) {
  const { points, selectedIndex, position, selectPoint, scrubTo } = timeline;
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const handleTrackInteraction = useCallback(
    (clientX: number) => {
      if (!trackRef.current || points.length < 2) return;
      const rect = trackRef.current.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const idx = Math.round(pct * (points.length - 1));
      return idx;
    },
    [points.length],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      const idx = handleTrackInteraction(e.clientX);
      if (idx != null) scrubTo(idx);
    },
    [handleTrackInteraction, scrubTo],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const idx = handleTrackInteraction(e.clientX);
      setHoverIndex(idx ?? null);
    },
    [handleTrackInteraction],
  );

  if (points.length === 0) return null;

  const previewPoint = hoverIndex !== null ? points[hoverIndex] : null;

  return (
    <div
      ref={trackRef}
      data-testid="lineage-track"
      className="relative h-8 cursor-pointer group"
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHoverIndex(null)}
    >
      {/* Track line */}
      <div className="absolute left-0 right-0 top-1/2 h-px bg-[var(--e-border-secondary)]" />

      {/* Point markers */}
      {points.map((point, idx) => {
        const pct = points.length > 1
          ? (idx / (points.length - 1)) * 100
          : 50;
        const isSelected = idx === selectedIndex;
        const isHovered = idx === hoverIndex;
        const isLive = idx === 0 && position === 'live';

        return (
          <button
            key={point.runId}
            type="button"
            data-testid={`lineage-track-point-${point.runId}`}
            onClick={(e) => {
              e.stopPropagation();
              selectPoint(idx);
            }}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-[1] transition-transform"
            style={{ left: `${pct}%` }}
            title={`Run ${point.runId.slice(0, 8)} · ${formatTime(point.timestamp)}`}
          >
            <div
              className={`rounded-full transition-all ${
                isSelected || isLive
                  ? 'h-3.5 w-3.5'
                  : isHovered
                    ? 'h-3 w-3'
                    : 'h-2 w-2'
              }`}
              style={{
                background: isSelected
                  ? 'var(--e-purple-500)'
                  : isLive
                    ? 'var(--e-green-600)'
                    : STATUS_COLORS[point.status] ?? 'var(--e-text-muted)',
                boxShadow: isSelected || isLive
                  ? `0 0 0 2px var(--e-bg-card), 0 0 0 3px ${isSelected ? 'var(--e-purple-500)' : 'var(--e-green-600)'}`
                  : undefined,
              }}
            />
          </button>
        );
      })}

      {/* Hover preview tooltip */}
      {previewPoint && hoverIndex !== selectedIndex && (
        <div
          className="absolute -top-8 -translate-x-1/2 pointer-events-none z-10 rounded px-2 py-1 bg-[var(--e-bg-card)] border border-[var(--e-border-secondary)] shadow-sm"
          style={{ left: `${points.length > 1 ? (hoverIndex! / (points.length - 1)) * 100 : 50}%` }}
        >
          <span className="text-[8px] font-mono text-[var(--e-text-secondary)] whitespace-nowrap">
            {formatAge(previewPoint.timestamp)}
            {previewPoint.compositeScore != null ? ` · ${previewPoint.compositeScore.toFixed(3)}` : ''}
          </span>
        </div>
      )}
    </div>
  );
}
